import classNames from 'classnames';
import type { CSSProperties } from 'react';
import { ITEM_RARITY_COLORS, ITEM_RARITY_NAMES, type GearComparison, type ItemRarity } from '@sorrem/shared';
import { Icon, type IconName } from '@sorrem/ui';
import styles from './ItemSlotMarks.module.scss';

interface ItemSlotMarksProps {
    /** The item's rarity; no tier shows without one (a plain catalog copy, a crystal stack) or for Hollow. */
    rarity: ItemRarity | undefined;
    /** How the item compares with what its wearer has on (#293); no mark when about even. */
    comparison?: GearComparison;
    /** Places the marks over the frame's box (the part of the art that is the frame, not a label plate). */
    className?: string;
}

/** The compare badge for each grade: green chevrons up for a gain, red down for a loss, two for a big one. */
const COMPARISON_MARKS: Readonly<Record<GearComparison, { icon: IconName; label: string; isGain: boolean }>> = {
    'much-better': { icon: 'double-arrow-up', label: 'Much better than what you wear', isGain: true },
    better: { icon: 'arrow-up', label: 'Better than what you wear', isGain: true },
    worse: { icon: 'arrow-down', label: 'Worse than what you wear', isGain: false },
    'much-worse': { icon: 'double-arrow-down', label: 'Much worse than what you wear', isGain: false },
};

/**
 * What a frame says about the item it holds (#341), drawn over the frame:
 * its tier, as a light rising from the frame's foot in the rarity's color
 * (stronger each tier) and the rarity's sigil (the mark its loot beam lays
 * on the floor) in a little frame on the top-left corner; and how it
 * compares with what is worn, as chevrons in a little frame on the top-right
 * corner. Hollow shows no tier. Each badge names what it means while the
 * pointer rests on it. Fills its positioned parent unless `className`
 * places it; put it inside the frame's button, so a click on a badge still
 * presses the frame.
 */
export function ItemSlotMarks({ rarity, comparison, className }: ItemSlotMarksProps): JSX.Element | null {
    const tier = rarity === 'common' ? undefined : rarity;
    const mark = comparison === undefined ? undefined : COMPARISON_MARKS[comparison];
    if (tier === undefined && mark === undefined) {
        return null;
    }

    const style = tier === undefined ? undefined : { '--rarity-color': ITEM_RARITY_COLORS[tier] } as CSSProperties;
    return (
        <span className={classNames(styles.itemSlotMarks, tier !== undefined && styles[tier], className)} style={style}>
            {tier !== undefined && (
                <>
                    <span className={styles.light} />
                    <span className={classNames(styles.badge, styles.tier)} role='img' aria-label={ITEM_RARITY_NAMES[tier]} title={`${ITEM_RARITY_NAMES[tier]} item`}>
                        <span className={styles.sigil} />
                    </span>
                </>
            )}
            {mark !== undefined && (
                <span className={classNames(styles.badge, styles.comparison, mark.isGain ? styles.gain : styles.loss)} role='img' aria-label={mark.label} title={mark.label}>
                    <Icon icon={mark.icon} strokeWidth={3} />
                </span>
            )}
        </span>
    );
}
